import { config } from 'src/utils/config';
import { UploadLink } from 'src/types/TUploadLink';
import { getStandardHeaders, getUploadHeaders, performFetchWithLogging } from '../utils/helpers';

// Upload links (mcp#131): the page an AI agent sends the user to. The link is
// an intent, not a credential: every call here uses the signed-in user's JWT.

export function getUploadLink(linkId: string, accessToken?: string) {
  const url = `${config.hostUrl}/api/v1/upload_links/${encodeURIComponent(linkId)}`;
  return performFetchWithLogging<UploadLink>(url, { headers: getStandardHeaders(accessToken) });
}

export function closeUploadLink(linkId: string, accessToken?: string) {
  const url = `${config.hostUrl}/api/v1/upload_links/${encodeURIComponent(linkId)}/close`;
  return performFetchWithLogging<UploadLink>(url, { method: 'POST', headers: getStandardHeaders(accessToken) });
}

export type UploadLinkFileError = { status: number; message: string };

export type UploadLinkFileRequest = {
  linkId: string;
  file: File;
  // folder-relative path, set only for folder picks
  path?: string;
  accessToken?: string;
  onProgress?: (fraction: number) => void;
};

/**
 * Posts one file to the upload server's multipart upload with the link id.
 * XMLHttpRequest rather than fetch, because fetch reports no upload progress.
 * The backend applies the link's metadata and collection; the page only says
 * where the file came from. Resolves with the new asset id.
 */
export function uploadFileToLink({ linkId, file, path, accessToken, onProgress }: UploadLinkFileRequest) {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<string>((resolve, reject) => {
    const url = `${config.uploadServerUrl}/api/v1/assets/new/content/original/upload?upload_link_id=${encodeURIComponent(linkId)}`;
    xhr.open('POST', url);
    Object.entries(getUploadHeaders(accessToken)).forEach(([key, value]) => xhr.setRequestHeader(key, value));
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const assetId = JSON.parse(xhr.responseText)?.files?.[0]?.id;
          if (assetId) {
            resolve(assetId);
            return;
          }
        } catch {
          // fall through to the error below
        }
        reject({ status: xhr.status, message: 'The upload finished but returned no asset' } as UploadLinkFileError);
        return;
      }
      reject({ status: xhr.status, message: uploadErrorMessage(xhr.responseText) } as UploadLinkFileError);
    };
    xhr.onerror = () => reject({ status: 0, message: 'Network error' } as UploadLinkFileError);
    xhr.onabort = () => reject({ status: 0, message: 'Cancelled' } as UploadLinkFileError);

    const form = new FormData();
    form.append('file', file, file.name);
    form.append('custom_metadata', JSON.stringify(path ? { source: { path } } : {}));
    xhr.send(form);
  });
  return { promise, abort: () => xhr.abort() };
}

// The upload answers {"status": "ERROR", "message": "..."} or plain text.
function uploadErrorMessage(body: string) {
  try {
    const parsed = JSON.parse(body);
    if (typeof parsed === 'string') return parsed;
    if (parsed?.message) return String(parsed.message);
  } catch {
    // not JSON
  }
  return body?.trim() || 'Upload failed';
}
